import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./index.scss";
import * as _ from "lodash";
import Dropdown from "../../../../../components/core/dropdown";
import Table from "../../../../../components/core/table";
import Popup from "../../../../../components/core/popup";
import { useTranslation } from "react-i18next";
import {
  addProfileShorthand,
  editProfileShorthand,
  listProfileShorthandByProfileId,
  removeProfileShorthand,
} from "../../../../../store/profile-shorthand/thunkAction";
import {
  sendNotifyIziMessageError,
  sendNotifyIziMessageSuccess,
} from "../../../../../utils/event";

const ProfileShorthand = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const profileId = _.get(params, "id");
  const [isOpen, setIsOpen] = useState(false);
  const [shorthand, setShorthand] = useState({});
  const [key, setKey] = useState("");
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!search) {
      dispatch(listProfileShorthandByProfileId({ profileId, search }));

      return;
    }

    const searchTimeout = setTimeout(() => {
      dispatch(listProfileShorthandByProfileId({ profileId, search }));
    }, 1000);

    return () => clearTimeout(searchTimeout);
  }, [dispatch, profileId, search]);

  const profileShorthandManage = useSelector(
    (state) => state.profileShorthandManage
  );
  const profileShorthands = _.get(
    profileShorthandManage,
    "profileShorthands",
    []
  );
  const isLoadingAddProfileShorthand = _.get(
    profileShorthands,
    "isLoadingAddProfileShorthand",
    false
  );

  useEffect(() => {
    setKey(_.get(shorthand, "key", ""));
    setText(_.get(shorthand, "text", ""));
  }, [shorthand]);

  const closePopup = () => {
    setIsOpen(false);
  };

  const validateShorthand = () => {
    if (!key && !key.trim()) {
      sendNotifyIziMessageError(t("profile.key_is_require"));
      return false;
    }
    if (!text && !text.trim()) {
      sendNotifyIziMessageError(t("profile.text_is_require"));
      return false;
    }
    return true;
  };

  const saveShorthand = async () => {
    const isOk = validateShorthand();
    if (!isOk) {
      return;
    }
    const shorthandId = _.get(shorthand, "_id");

    if (shorthandId) {
      try {
        await dispatch(
          editProfileShorthand({
            id: shorthandId,
            data: {
              key,
              text,
            },
          })
        ).unwrap();
        sendNotifyIziMessageSuccess(
          t("profile.profile_shorthand_save_success")
        );
      } catch (error) {
        sendNotifyIziMessageError(t("profile.profile_shorthand_save_error"));
      }

      dispatch(listProfileShorthandByProfileId({ profileId, search }));
      closePopup();
      return;
    }

    const isExistsKey = _.findIndex(
      profileShorthands,
      (profileShorthand) => profileShorthand.key === key
    );

    if (isExistsKey >= 0) {
      sendNotifyIziMessageError(t(`profile.key_is_exists`));
      return;
    }

    const data = {
      profileId,
      key,
      text,
    };

    if (isLoadingAddProfileShorthand) {
      return;
    }

    try {
      await dispatch(addProfileShorthand(data)).unwrap();
      sendNotifyIziMessageSuccess(t("profile.profile_shorthand_save_success"));
    } catch (error) {
      sendNotifyIziMessageError(t("profile.profile_shorthand_save_error"));
    }

    dispatch(listProfileShorthandByProfileId({ profileId, search }));
    closePopup();
  };

  const removeShorthand = async (shorthand) => {
    const shorthandId = _.get(shorthand, "_id");

    if (!shorthandId) return;

    try {
      await dispatch(removeProfileShorthand(shorthandId)).unwrap();
      sendNotifyIziMessageSuccess(
        t("profile.profile_shorthand_remove_success")
      );
    } catch (error) {
      sendNotifyIziMessageError(t("profile.profile_shorthand_remove_error"));
    }
    dispatch(listProfileShorthandByProfileId({ profileId, search }));
  };

  const configTable = {
    header: [
      {
        key: "no",
        label: "NO.",
        className: "text-center small",
        render: (row, rowIndex) => rowIndex + 1,
      },
      {
        key: "key",
        label: t("settings.profile.shorthands.key"),
        className: "small",
      },
      {
        key: "message",
        label: t("settings.profile.shorthands.message"),
        className: "small",
        render: (row) => {
          const message = _.get(row, "text", "");

          return message;
        },
      },
      {
        key: "manage",
        label: t("settings.profile.shorthands.manage"),
        className: "text-center small",
        render: (row) => (
          <div className="d-flex gap-4 align-items-center justify-content-center">
            <button
              style={{ background: "none", border: "none" }}
              className="color-dark"
              onClick={() => {
                setShorthand(row);
                setIsOpen(true);
              }}
            >
              <i className="fa fa-pencil-alt"></i>
            </button>
            <button
              style={{ background: "none", border: "none" }}
              className="color-red"
              onClick={() => removeShorthand(row)}
            >
              <i className="fa fa-trash-alt"></i>
            </button>
          </div>
        ),
      },
    ],
    collection: profileShorthands,
  };

  return (
    <div className="container">
      <div className="card bg-white border-0">
        <div className="card-body">
          <div className="row header-title mb-4">
            <div className="col-lg-6">
              <h3 className="color-pri">
                {t("settings.profile.shorthands.title")}
              </h3>
            </div>
            <div className="col-lg-6 text-start text-lg-end align-self-center">
              <a
                className="default-link"
                href={`#/profile/${profileId}/detail`}
              >
                <i className="fa fa-arrow-left"></i>{" "}
                {t("settings.profile.profile_detail")}
              </a>
            </div>
            <div className="col-12 color-gray small">
              {t("settings.profile.shorthands.desc")}
            </div>
          </div>

          <div className="row mb-3 g-3">
            <div className="col-lg-4">
              <div className="input-icon">
                <i className="fa fa-search"></i>
                <input
                  type="text"
                  className="form-control has-icon search"
                  placeholder={t("settings.profile.shorthands.search")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="col-lg-4"></div>
            <div className="col-lg-2">
              <div className="dropdown">
                {/* <Dropdown
                  config={{
                    options: [
                      { name: "Tùy chọn 1", id: "option1" },
                      { name: "Tùy chọn 2", id: "option2" },
                      { name: "Tùy chọn 3", id: "option3" },
                    ],
                    onSelect: handleSelect,
                    placeholder: "Chọn tùy chọn...",
                    className: "button-status"
                  }}
                /> */}
              </div>
            </div>
            <div className="col-lg-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => {
                  setShorthand({});
                  setIsOpen(true);
                }}
              >
                {t("common.create")}
              </button>
            </div>
          </div>
          <div className="table-responsive table-box mb-5">
            <Table config={configTable} />
          </div>
        </div>
      </div>
      <Popup
        config={{
          isOpen: isOpen,
          onClose: closePopup,
        }}
      >
        <div className="modal-content">
          <div className="modal-body">
            <div className="row g-4">
              <div className="col-12">
                <label className="form-label text-uppercase">
                  {" "}
                  {t("settings.profile.shorthands.key")}
                </label>
                <input
                  className="form-control"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                />
                <div className="color-gray mt-2">
                  {t("settings.profile.shorthands.key_desc")}
                </div>
              </div>
              <div className="col-12">
                <label className="form-label text-uppercase">
                  {t("settings.profile.shorthands.message")}
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                ></textarea>
                <div className="color-gray mt-2">
                  {t("settings.profile.shorthands.message_desc")}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-gray btn-outline-gray"
              onClick={closePopup}
            >
              {t("common.closed")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                saveShorthand();
              }}
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default ProfileShorthand;
